import { useTranslation } from 'react-i18next'
import { REGIE_LINE_KINDS, regieStunden, regieStundenText, type RegieLineKind } from '@symphenia/shared-types'
import type { ErpVorzeigeProps } from '../registry'

interface KanonZeile {
  art: RegieLineKind
  beschreibung: string
  menge: string
  einheit: string
  person?: string
  qualifikation?: string
  zuschlag?: string
  von?: string
  bis?: string
  artikel?: { nummer: string | null; bezeichnung: string | null }
}

/**
 * AP-27 9. — **Vorzeige und eingefrorener Stand des Regieberichts im ERP**: aus dem kanonischen
 * Fachteil — Beschreibung, Anordnung, Zeilen je Art mit Zuschlag, Stunden je Zuschlag.
 */
export function RegieErpVorzeige({ fachteil }: ErpVorzeigeProps) {
  const { t } = useTranslation()
  const f = fachteil as { beschreibung?: string; anordnung?: { von: string; funktion?: string; am?: string }; zeilen?: KanonZeile[] }
  const zeilen = f.zeilen ?? []
  const stunden = regieStunden(zeilen.map((z) => ({ kind: z.art, quantity: z.menge, surchargeLabel: z.zuschlag ?? null })))
  const menge = (m: string) => String(Number(m)).replace('.', ',')
  return (
    <div className="space-y-3 text-sm">
      {f.beschreibung && <p className="whitespace-pre-line">{f.beschreibung}</p>}
      {f.anordnung?.von && (
        <p>
          <span className="text-muted-foreground">{t('siteDocs.regie.orderedBy')}:</span> {f.anordnung.von}
          {f.anordnung.funktion ? `, ${f.anordnung.funktion}` : ''}
          {f.anordnung.am ? ` · ${f.anordnung.am.split('-').reverse().join('.')}` : ''}
        </p>
      )}
      {REGIE_LINE_KINDS.map((g) => {
        const liste = zeilen.filter((z) => z.art === g)
        if (!liste.length) return null
        return (
          <section key={g}>
            <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t(`siteDocs.regie.groups.${g}`)}</h4>
            <table className="w-full">
              <tbody>
                {liste.map((z, i) => (
                  <tr key={i} className="border-b border-border last:border-0">
                    <td className="py-1 pr-2">
                      <span className="font-medium">{g === 'SERVICE' ? z.person ?? z.beschreibung : z.artikel?.bezeichnung ?? z.beschreibung}</span>
                      <span className="ml-2 text-xs text-muted-foreground">
                        {[g === 'SERVICE' ? z.beschreibung : null, z.qualifikation, z.von && z.bis ? `${z.von}–${z.bis}` : null, z.zuschlag, z.artikel?.nummer].filter(Boolean).join(' · ')}
                      </span>
                    </td>
                    <td className="whitespace-nowrap py-1 text-right tabular-nums">
                      {menge(z.menge)} {z.einheit}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )
      })}
      {stunden.gesamt > 0 && (
        <p>
          <strong>
            {t('siteDocs.regie.totalHours')}: {regieStundenText(stunden.gesamt)} h
          </strong>
          {(stunden.jeZuschlag.length > 1 || stunden.jeZuschlag[0]?.zuschlag) && (
            <span className="ml-2 text-muted-foreground">{stunden.jeZuschlag.map((x) => `${x.zuschlag ?? t('siteDocs.regie.noSurcharge')}: ${regieStundenText(x.stunden)} h`).join(' · ')}</span>
          )}
        </p>
      )}
    </div>
  )
}
